import React, { useRef, useState, useCallback } from "react";
import { WORLD_W, WORLD_H, LAYERS, ITEM_KINDS, RUN_KINDS, uid, snap, roomArea } from "../lib/blueprint";
import { computeSetback } from "../lib/setback";
import { insetPolygon, pointsAttr, convexHull } from "../lib/geo";

export default function CanvasGrid({ blueprint, setBlueprint, tool, activeLayers, selectedId, setSelectedId, pxPerFt, readOnly = false, comments, commentMode = false, onAddComment, heatmap = false, solarScores, solarHours, sunPath = false, season = "Equinox", blockedItems, sectionLine, showSetbacks = false, setbacks, lot = null, setLot, siteMapUrl = null, siteMapLot = null, worldW = WORLD_W, worldH = WORLD_H, boundary = null, showShadows = false, shadow = null, driveway = null, topOfSlope = null, downhillZone = null, cantilever = null, dimGuard = false }) {
  const blocked = blockedItems instanceof Set ? blockedItems : new Set(blockedItems || []);
  const sbCalc = setbacks ? computeSetback(blueprint.rooms, setbacks, lot) : null; // always → live violations
  const sb = showSetbacks ? sbCalc : null; // overlay drawing only when toggled on
  const poly = boundary && boundary.length >= 3 ? boundary : null;
  const buildablePoly = poly && setbacks ? insetPolygon(poly, Math.max(Number(setbacks.side) || 0, Number(setbacks.front) || 0, 3)) : null;
  const svgRef = useRef(null);
  const [draft, setDraft] = useState(null); // room being drawn
  const [wallDraft, setWallDraft] = useState(null); // wall being drawn
  const [runDraft, setRunDraft] = useState(null); // MEP run being drawn
  const drag = useRef(null); // {mode, id, offX, offY}

  const W = worldW * pxPerFt;
  const H = worldH * pxPerFt;

  const toFt = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / pxPerFt;
    const y = (e.clientY - rect.top) / pxPerFt;
    return {
      x: Math.min(Math.max(0, x), worldW),
      y: Math.min(Math.max(0, y), worldH),
    };
  }, [pxPerFt, worldW, worldH]);

  const placeItem = (pt) => {
    const kind = tool;
    const meta = ITEM_KINDS[kind];
    const layer = meta.layer;
    if (meta.furniture) {
      setBlueprint((b) => ({ ...b, items: [...b.items, { id: uid(), kind, layer, x: snap(pt.x), y: snap(pt.y), w: meta.w, h: meta.h }] }));
      return;
    }
    const p = snapItem(pt, kind);
    setBlueprint((b) => ({ ...b, items: [...b.items, { id: uid(), kind, layer, x: p.x, y: p.y }] }));
  };

  // Nearest point on any room wall edge
  const nearestWall = (pt) => {
    let best = null;
    for (const r of blueprint.rooms) {
      const edges = [
        { x1: r.x, y1: r.y, x2: r.x + r.w, y2: r.y },
        { x1: r.x, y1: r.y + r.h, x2: r.x + r.w, y2: r.y + r.h },
        { x1: r.x, y1: r.y, x2: r.x, y2: r.y + r.h },
        { x1: r.x + r.w, y1: r.y, x2: r.x + r.w, y2: r.y + r.h },
      ];
      for (const e of edges) {
        const dx = e.x2 - e.x1, dy = e.y2 - e.y1;
        const len2 = dx * dx + dy * dy || 1;
        let t = ((pt.x - e.x1) * dx + (pt.y - e.y1) * dy) / len2;
        t = Math.max(0, Math.min(1, t));
        const px = e.x1 + t * dx, py = e.y1 + t * dy;
        const d = Math.hypot(pt.x - px, pt.y - py);
        if (!best || d < best.d) best = { d, px, py };
      }
    }
    // also snap onto any explicit wall segments
    for (const w of (blueprint.walls || [])) {
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy || 1;
      let t = ((pt.x - w.x1) * dx + (pt.y - w.y1) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const px = w.x1 + t * dx, py = w.y1 + t * dy;
      const d = Math.hypot(pt.x - px, pt.y - py);
      if (!best || d < best.d) best = { d, px, py };
    }
    return best;
  };

  // Snap an item to the nearest wall (doors/windows pull harder), else to grid
  const snapItem = (pt, kind) => {
    const th = (kind === "door" || kind === "window") ? 4 : 2;
    const w = nearestWall(pt);
    if (w && w.d < th) return { x: snap(w.px), y: snap(w.py) };
    return { x: snap(pt.x), y: snap(pt.y) };
  };

  // Align a moving room's edges to other rooms' edges / world bounds
  const alignMove = (id, nx, ny, w, h) => {
    const T = 1.2;
    const others = blueprint.rooms.filter((r) => r.id !== id);
    const vx = [0, worldW]; const hy = [0, worldH];
    others.forEach((r) => { vx.push(r.x, r.x + r.w); hy.push(r.y, r.y + r.h); });
    if (poly) poly.forEach((v) => { vx.push(v.x); hy.push(v.y); });
    let sx = snap(nx), sy = snap(ny);
    for (const g of vx) { if (Math.abs(nx - g) < T) { sx = g; break; } if (Math.abs(nx + w - g) < T) { sx = g - w; break; } }
    for (const g of hy) { if (Math.abs(ny - g) < T) { sy = g; break; } if (Math.abs(ny + h - g) < T) { sy = g - h; break; } }
    // snap a room corner onto the nearest angled property-line vertex
    if (poly) {
      const VT = 3;
      let best = null;
      const corners = [[sx, sy], [sx + w, sy], [sx, sy + h], [sx + w, sy + h]];
      poly.forEach((v) => corners.forEach(([cx, cy]) => {
        const d = Math.hypot(cx - v.x, cy - v.y);
        if (d < VT && (!best || d < best.d)) best = { d, dx: v.x - cx, dy: v.y - cy };
      }));
      if (best) { sx += best.dx; sy += best.dy; }
    }
    return { x: Math.min(Math.max(0, sx), worldW - w), y: Math.min(Math.max(0, sy), worldH - h) };
  };

  // Align a resizing room's right/bottom edges to other rooms' edges
  const alignResize = (id, r, ptx, pty) => {
    const T = 1.2;
    const others = blueprint.rooms.filter((x) => x.id !== id);
    const vx = [worldW]; const hy = [worldH];
    others.forEach((o) => { vx.push(o.x, o.x + o.w); hy.push(o.y, o.y + o.h); });
    let right = ptx, bottom = pty;
    for (const g of vx) { if (Math.abs(ptx - g) < T) { right = g; break; } }
    for (const g of hy) { if (Math.abs(pty - g) < T) { bottom = g; break; } }
    return { w: snap(Math.max(2, Math.min(right - r.x, worldW - r.x))), h: snap(Math.max(2, Math.min(bottom - r.y, worldH - r.y))) };
  };

  const onCanvasDown = (e) => {
    if (commentMode) { const p = toFt(e); onAddComment && onAddComment(p.x, p.y); return; }
    if (readOnly) return;
    const pt = toFt(e);
    if (tool === "room") {
      setDraft({ x: pt.x, y: pt.y, w: 0, h: 0, sx: pt.x, sy: pt.y });
      return;
    }
    if (tool === "wall") {
      setWallDraft({ x1: snap(pt.x), y1: snap(pt.y), x2: snap(pt.x), y2: snap(pt.y) });
      return;
    }
    if (RUN_KINDS[tool]) {
      setRunDraft({ tool, x1: snap(pt.x), y1: snap(pt.y), x2: snap(pt.x), y2: snap(pt.y) });
      return;
    }
    if (ITEM_KINDS[tool]) { placeItem(pt); return; }
    if (tool === "select") { setSelectedId(null); }
  };

  const onMove = (e) => {
    const pt = toFt(e);
    if (draft) {
      setDraft((d) => ({ ...d, x: Math.min(d.sx, pt.x), y: Math.min(d.sy, pt.y), w: Math.abs(pt.x - d.sx), h: Math.abs(pt.y - d.sy) }));
      return;
    }
    if (wallDraft) {
      setWallDraft((d) => ({ ...d, x2: snap(pt.x), y2: snap(pt.y) }));
      return;
    }
    if (runDraft) {
      setRunDraft((d) => ({ ...d, x2: snap(pt.x), y2: snap(pt.y) }));
      return;
    }
    if (drag.current) {
      const { mode, id } = drag.current;
      if (mode === "move-room") {
        setBlueprint((b) => ({ ...b, rooms: b.rooms.map((r) => {
          if (r.id !== id) return r;
          const a = alignMove(id, pt.x - drag.current.offX, pt.y - drag.current.offY, r.w, r.h);
          return { ...r, x: a.x, y: a.y };
        }) }));
      } else if (mode === "resize-room") {
        setBlueprint((b) => ({ ...b, rooms: b.rooms.map((r) => {
          if (r.id !== id) return r;
          const a = alignResize(id, r, pt.x, pt.y);
          return { ...r, w: a.w, h: a.h };
        }) }));
      } else if (mode === "move-item") {
        const it = blueprint.items.find((x) => x.id === id);
        const p = snapItem(pt, it ? it.kind : "");
        setBlueprint((b) => ({ ...b, items: b.items.map((x) => x.id === id ? { ...x, x: p.x, y: p.y } : x) }));
      } else if (mode === "lot-corner" && setLot && sb) {
        const L = sb.lot;
        const fx = drag.current.corner.includes("w") ? L.x + L.w : L.x; // fixed x edge
        const fy = drag.current.corner.includes("n") ? L.y + L.h : L.y; // fixed y edge
        const nx = Math.max(0, snap(pt.x)), ny = Math.max(0, snap(pt.y));
        const x0 = Math.min(fx, nx), y0 = Math.min(fy, ny);
        setLot({ x: x0, y: y0, w: Math.max(10, Math.abs(nx - fx)), h: Math.max(10, Math.abs(ny - fy)) });
      }
    }
  };

  const onUp = () => {
    if (draft) {
      if (draft.w >= 2 && draft.h >= 2) {
        const nr = { id: uid(), name: "Room", x: snap(draft.x), y: snap(draft.y), w: snap(draft.w), h: snap(draft.h) };
        setBlueprint((b) => ({ ...b, rooms: [...b.rooms, nr] }));
        setSelectedId(nr.id);
      }
      setDraft(null);
    }
    if (wallDraft) {
      const { x1, y1, x2, y2 } = wallDraft;
      if (Math.hypot(x2 - x1, y2 - y1) >= 1) {
        const nw = { id: uid(), x1, y1, x2, y2, thickness: 0.4 };
        setBlueprint((b) => ({ ...b, walls: [...(b.walls || []), nw] }));
        setSelectedId(nw.id);
      }
      setWallDraft(null);
    }
    if (runDraft) {
      const { tool: rtool, x1, y1, x2, y2 } = runDraft;
      if (Math.hypot(x2 - x1, y2 - y1) >= 1) {
        const nr = { id: uid(), tool: rtool, x1, y1, x2, y2 };
        setBlueprint((b) => ({ ...b, runs: [...(b.runs || []), nr] }));
        setSelectedId(nr.id);
      }
      setRunDraft(null);
    }
    drag.current = null;
  };

  const startRoomDrag = (e, r, mode) => {
    if (readOnly || tool !== "select") return;
    e.stopPropagation();
    setSelectedId(r.id);
    const pt = toFt(e);
    drag.current = { mode, id: r.id, offX: pt.x - r.x, offY: pt.y - r.y };
  };

  const startItemDrag = (e, it) => {
    if (readOnly || tool !== "select") return;
    e.stopPropagation();
    setSelectedId(it.id);
    drag.current = { mode: "move-item", id: it.id };
  };

  const startLotDrag = (e, corner) => {
    if (readOnly) return;
    e.stopPropagation();
    drag.current = { mode: "lot-corner", corner };
  };

  // Compute drawn-wall sub-segments broken at door/window openings
  const wallSegments = (w) => {
    const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L, nx = -uy, ny = ux;
    const ops = [];
    (blueprint.items || []).forEach((it) => {
      if (it.kind !== "door" && it.kind !== "window") return;
      const rx = it.x - w.x1, ry = it.y - w.y1;
      const t = rx * ux + ry * uy;
      const perp = Math.abs(rx * nx + ry * ny);
      if (perp < 0.8 && t > 0.4 && t < L - 0.4) ops.push({ a: Math.max(0, t - 1.5), b: Math.min(L, t + 1.5) });
    });
    // explicit openings cut directly into the wall (center + width, in feet from start)
    (w.openings || []).forEach((o) => {
      const cen = typeof o.center === "number" ? o.center : (typeof o.t === "number" ? o.t * L : null);
      const wid = Number(o.width) || 3;
      if (cen == null) return;
      ops.push({ a: Math.max(0, cen - wid / 2), b: Math.min(L, cen + wid / 2) });
    });
    ops.sort((p, q) => p.a - q.a);
    const segs = []; let cur = 0;
    ops.forEach((o) => { if (o.a > cur) segs.push([cur, o.a]); cur = Math.max(cur, o.b); });
    if (cur < L) segs.push([cur, L]);
    const pt = (t) => ({ x: (w.x1 + ux * t) * pxPerFt, y: (w.y1 + uy * t) * pxPerFt });
    return {
      segs: segs.map(([a, b]) => ({ p1: pt(a), p2: pt(b) })),
      ops: ops.map((o) => ({ p1: pt(o.a), p2: pt(o.b) })),
      n: { x: nx, y: ny },
    };
  };

  return (
    <div className="w-full h-full overflow-auto grid-dots" data-testid="cad-canvas-container">
      <svg
        ref={svgRef}
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        id="blueprint-svg"
        style={{ display: "block", margin: "40px auto", cursor: commentMode ? "crosshair" : ((readOnly || tool === "select") ? "default" : "crosshair"), background: "#0B1017", boxShadow: "0 0 0 1px #30363D, 0 30px 80px rgba(0,0,0,0.5)" }}
        onPointerDown={onCanvasDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        {/* grid */}
        <defs>
          <pattern id="grid" width={pxPerFt} height={pxPerFt} patternUnits="userSpaceOnUse">
            <path d={`M ${pxPerFt} 0 L 0 0 0 ${pxPerFt}`} fill="none" stroke="rgba(56,189,248,0.08)" strokeWidth="1" />
          </pattern>
          <pattern id="grid5" width={pxPerFt * 5} height={pxPerFt * 5} patternUnits="userSpaceOnUse">
            <path d={`M ${pxPerFt * 5} 0 L 0 0 0 ${pxPerFt * 5}`} fill="none" stroke="rgba(56,189,248,0.16)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="url(#grid)" />
        <rect width={W} height={H} fill="url(#grid5)" />

        {/* site map background layer (faint, so you trace the house on the land) */}
        {siteMapUrl && (
          <image href={siteMapUrl} data-testid="canvas-sitemap-bg"
            x={(siteMapLot?.x || 0) * pxPerFt} y={(siteMapLot?.y || 0) * pxPerFt}
            width={(siteMapLot?.w || worldW) * pxPerFt} height={(siteMapLot?.h || worldH) * pxPerFt}
            preserveAspectRatio="xMidYMid meet" opacity="0.4" style={{ pointerEvents: "none" }} />
        )}

        {/* driveway apron (paved strip from street to garage) */}
        {driveway && (
          <g data-testid="canvas-driveway" style={{ pointerEvents: "none" }}>
            <rect x={driveway.x * pxPerFt} y={driveway.y * pxPerFt} width={driveway.w * pxPerFt} height={driveway.h * pxPerFt}
              fill="#6E7681" fillOpacity="0.22" stroke="#8B949E" strokeWidth="1" strokeDasharray="5 4" rx="2" />
            <text x={(driveway.x + driveway.w / 2) * pxPerFt} y={(driveway.y + driveway.h / 2) * pxPerFt}
              fill="#C9D1D9" fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono, monospace"
              transform={driveway.h > driveway.w ? `rotate(-90 ${(driveway.x + driveway.w / 2) * pxPerFt} ${(driveway.y + driveway.h / 2) * pxPerFt})` : ""}>DRIVEWAY</text>
          </g>
        )}

        {/* downhill no-build zone + top-of-slope grade break */}
        {downhillZone && downhillZone.length >= 3 && (
          <polygon data-testid="canvas-downhill-zone" style={{ pointerEvents: "none" }}
            points={downhillZone.map((p) => `${p.x * pxPerFt},${p.y * pxPerFt}`).join(" ")}
            fill="rgba(245,158,11,0.12)" stroke="none" />
        )}
        {topOfSlope && topOfSlope.length >= 2 && (
          <g data-testid="canvas-top-of-slope" style={{ pointerEvents: "none" }}>
            <polyline points={topOfSlope.map((p) => `${p.x * pxPerFt},${p.y * pxPerFt}`).join(" ")}
              fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="8 5" />
            <text x={topOfSlope[0].x * pxPerFt + 6} y={topOfSlope[0].y * pxPerFt - 6}
              fill="#F59E0B" fontSize="12" fontWeight="800" fontFamily="JetBrains Mono, monospace">TOP OF SLOPE — NO BUILD DOWNHILL</text>
          </g>
        )}
        {cantilever && (
          <g data-testid="canvas-cantilever" style={{ pointerEvents: "none" }}>
            <rect x={cantilever.x * pxPerFt} y={cantilever.y * pxPerFt} width={cantilever.w * pxPerFt} height={cantilever.h * pxPerFt}
              fill="rgba(56,189,248,0.10)" stroke="#38BDF8" strokeWidth="2" strokeDasharray="6 4" />
            <text x={(cantilever.x + cantilever.w / 2) * pxPerFt} y={(cantilever.y + cantilever.h / 2) * pxPerFt}
              fill="#38BDF8" fontSize="11" fontWeight="800" textAnchor="middle" fontFamily="JetBrains Mono, monospace">CANTILEVER</text>
          </g>
        )}

        {/* sun path arc (E sunrise -> S noon -> W sunset) */}
        {sunPath && (() => {
          const eX = W * 0.92, wX = W * 0.08, endY = H * 0.34, cX = W * 0.5, cY = H * 0.96;
          const midX = 0.25 * eX + 0.5 * cX + 0.25 * wX;
          const midY = 0.25 * endY + 0.5 * cY + 0.25 * endY;
          return (
            <g style={{ pointerEvents: "none" }} data-testid="sun-path-overlay">
              <path d={`M ${eX} ${endY} Q ${cX} ${cY} ${wX} ${endY}`} fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="6 5" strokeOpacity="0.7" />
              {/* sun disc at solar noon */}
              <circle cx={midX} cy={midY} r="13" fill="#F59E0B" fillOpacity="0.22" stroke="#F59E0B" strokeWidth="1.5" />
              <circle cx={midX} cy={midY} r="6" fill="#FBBF24" />
              {[...Array(8)].map((_, k) => {
                const a = (k / 8) * Math.PI * 2;
                return <line key={k} x1={midX + Math.cos(a) * 9} y1={midY + Math.sin(a) * 9} x2={midX + Math.cos(a) * 13} y2={midY + Math.sin(a) * 13} stroke="#FBBF24" strokeWidth="1.5" />;
              })}
              <text x={midX} y={midY - 20} fill="#F59E0B" fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle">S · SOLAR NOON</text>
              <text x={eX + 6} y={endY - 6} fill="#F59E0B" fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="end">E ☼ sunrise</text>
              <text x={wX - 6} y={endY - 6} fill="#F59E0B" fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="start">sunset ☼ W</text>
              <text x={cX} y={16} fill="#8B949E" fontSize="9" fontWeight="600" fontFamily="JetBrains Mono, monospace" textAnchor="middle">SUN PATH · {season.toUpperCase()}</text>
            </g>
          );
        })()}


        {/* building shadows cast on the lot (from sun position + orientation) */}
        {showShadows && shadow && (shadow.dx || shadow.dy) && blueprint.rooms.length > 0 && (
          <g data-testid="shadow-overlay" style={{ pointerEvents: "none" }}>
            {blueprint.rooms.map((r) => {
              const base = [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
              const off = base.map((p) => ({ x: p.x + shadow.dx, y: p.y + shadow.dy }));
              const hull = convexHull([...base, ...off]);
              return <polygon key={r.id} points={pointsAttr(hull.map((p) => ({ x: p.x * pxPerFt, y: p.y * pxPerFt })))} fill="rgba(8,12,20,0.42)" />;
            })}
          </g>
        )}

        {/* rooms */}
        {activeLayers.structural && blueprint.rooms.map((r) => {
          const sel = r.id === selectedId;
          const violates = sbCalc && sbCalc.violationIds.has(r.id);
          const hasTarget = dimGuard && r.tw != null && r.th != null;
          const drift = hasTarget ? Math.max(Math.abs(r.w - r.tw), Math.abs(r.h - r.th)) : 0;
          const driftBad = hasTarget && drift > 0.25;
          const hs = heatmap && solarScores ? solarScores[r.id] : undefined;
          const hColor = hs === undefined ? null : (hs >= 60 ? "#34D399" : hs >= 32 ? "#F59E0B" : "#F43F5E");
          return (
            <g key={r.id}>
              <rect
                x={r.x * pxPerFt} y={r.y * pxPerFt} width={r.w * pxPerFt} height={r.h * pxPerFt}
                fill={hColor ? `${hColor}44` : (violates ? "rgba(244,63,94,0.10)" : (driftBad ? "rgba(245,158,11,0.10)" : (sel ? "rgba(56,189,248,0.10)" : "rgba(240,246,252,0.04)")))}
                stroke={violates ? "#F43F5E" : (driftBad ? "#F59E0B" : (hColor ? hColor : (sel ? "#38BDF8" : "#C9D1D9")))} strokeWidth={sel ? 3 : 2}
                strokeDasharray={driftBad && !violates ? "7 4" : undefined}
                onPointerDown={(e) => startRoomDrag(e, r, "move-room")}
                data-testid={`room-${r.id}`}
                style={{ cursor: tool === "select" ? "move" : "crosshair" }}
              />
              {violates && (
                <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt + 13} fill="#F43F5E" fontSize="9" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }} data-testid={`setback-violation-${r.id}`}>⚠ setback</text>
              )}
              {driftBad && !violates && (
                <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt + 13} fill="#F59E0B" fontSize="9" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }} data-testid={`dim-drift-${r.id}`}>⚠ off {drift.toFixed(1)}′ (target {r.tw}×{r.th})</text>
              )}
              {hColor && (
                <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt + r.h * pxPerFt - 8}
                  fill={hColor} fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }}>☀ {hs}</text>
              )}
              <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt + r.h * pxPerFt / 2 - 6}
                fill="#F0F6FC" fontSize="12" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle" style={{ pointerEvents: "none" }}>
                {r.name}
              </text>
              <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt + r.h * pxPerFt / 2 + 12}
                fill="#C9D1D9" fontSize="11" fontWeight="500" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }}>
                {`${r.w}′ × ${r.h}′ · ${roomArea(r)} ft²`}
              </text>
              {/* dimension top */}
              <text x={r.x * pxPerFt + r.w * pxPerFt / 2} y={r.y * pxPerFt - 4} fill="#6E7681" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }}>{`${r.w}′`}</text>
              {sunPath && solarHours && solarHours[r.id] !== undefined && (
                <text x={r.x * pxPerFt + 6} y={r.y * pxPerFt + 15} fill="#F59E0B" fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace" style={{ pointerEvents: "none" }} data-testid={`room-sunhours-${r.id}`}>☀ {solarHours[r.id]}h</text>
              )}
              {sel && tool === "select" && !readOnly && (
                <rect x={r.x * pxPerFt + r.w * pxPerFt - 7} y={r.y * pxPerFt + r.h * pxPerFt - 7} width="14" height="14"
                  fill="#38BDF8" stroke="#0D1117" strokeWidth="1.5" rx="2"
                  onPointerDown={(e) => startRoomDrag(e, r, "resize-room")}
                  style={{ cursor: "nwse-resize" }} data-testid={`resize-${r.id}`} />
              )}
            </g>
          );
        })}

        {/* traced / drawn walls with breaks at openings */}
        {activeLayers.structural && (blueprint.walls || []).map((w) => {
          const { segs, ops, n } = wallSegments(w);
          const sel = w.id === selectedId;
          const sw = Math.max(2, (w.thickness || 0.4) * pxPerFt);
          const col = sel ? "#38BDF8" : "#C9D1D9";
          return (
            <g key={w.id} data-testid={`wall-${w.id}`}>
              <line x1={w.x1 * pxPerFt} y1={w.y1 * pxPerFt} x2={w.x2 * pxPerFt} y2={w.y2 * pxPerFt}
                stroke="transparent" strokeWidth={sw + 12}
                onPointerDown={(e) => { if (tool === "select" && !readOnly) { e.stopPropagation(); setSelectedId(w.id); } }}
                style={{ cursor: tool === "select" ? "pointer" : "crosshair", pointerEvents: readOnly ? "none" : "stroke" }} />
              {segs.map((sg, i) => (
                <line key={i} x1={sg.p1.x} y1={sg.p1.y} x2={sg.p2.x} y2={sg.p2.y}
                  stroke={col} strokeWidth={sw} strokeLinecap="butt" style={{ pointerEvents: "none" }} />
              ))}
              {ops.map((o, i) => (
                <g key={`o${i}`} style={{ pointerEvents: "none" }}>
                  <line x1={o.p1.x - n.x * sw * 0.6} y1={o.p1.y - n.y * sw * 0.6} x2={o.p1.x + n.x * sw * 0.6} y2={o.p1.y + n.y * sw * 0.6} stroke={col} strokeWidth="1.5" />
                  <line x1={o.p2.x - n.x * sw * 0.6} y1={o.p2.y - n.y * sw * 0.6} x2={o.p2.x + n.x * sw * 0.6} y2={o.p2.y + n.y * sw * 0.6} stroke={col} strokeWidth="1.5" />
                </g>
              ))}
            </g>
          );
        })}

        {/* wall draft preview */}
        {wallDraft && (
          <line x1={wallDraft.x1 * pxPerFt} y1={wallDraft.y1 * pxPerFt} x2={wallDraft.x2 * pxPerFt} y2={wallDraft.y2 * pxPerFt}
            stroke="#38BDF8" strokeDasharray="5 4" strokeWidth="3" strokeLinecap="round" />
        )}

        {/* MEP duct / pipe runs */}
        {(blueprint.runs || []).map((r) => {
          const cfg = RUN_KINDS[r.tool];
          if (!cfg || !activeLayers[cfg.layer]) return null;
          const sel = r.id === selectedId;
          const sw = Math.max(2, cfg.width * pxPerFt);
          return (
            <g key={r.id} data-testid={`run-${r.id}`}>
              <line x1={r.x1 * pxPerFt} y1={r.y1 * pxPerFt} x2={r.x2 * pxPerFt} y2={r.y2 * pxPerFt}
                stroke="transparent" strokeWidth={sw + 12}
                onPointerDown={(e) => { if (tool === "select" && !readOnly) { e.stopPropagation(); setSelectedId(r.id); } }}
                style={{ cursor: tool === "select" ? "pointer" : "crosshair", pointerEvents: readOnly ? "none" : "stroke" }} />
              <line x1={r.x1 * pxPerFt} y1={r.y1 * pxPerFt} x2={r.x2 * pxPerFt} y2={r.y2 * pxPerFt}
                stroke={sel ? "#F0F6FC" : cfg.color} strokeWidth={sel ? sw + 1.5 : sw} strokeLinecap="round"
                strokeDasharray={cfg.dash ? cfg.dash.split(" ").map((n) => Number(n) * pxPerFt).join(" ") : undefined}
                style={{ pointerEvents: "none" }} />
              <circle cx={r.x1 * pxPerFt} cy={r.y1 * pxPerFt} r={sw * 0.6 + 1} fill={cfg.color} style={{ pointerEvents: "none" }} />
              <circle cx={r.x2 * pxPerFt} cy={r.y2 * pxPerFt} r={sw * 0.6 + 1} fill={cfg.color} style={{ pointerEvents: "none" }} />
            </g>
          );
        })}

        {/* run draft preview */}
        {runDraft && RUN_KINDS[runDraft.tool] && (
          <line x1={runDraft.x1 * pxPerFt} y1={runDraft.y1 * pxPerFt} x2={runDraft.x2 * pxPerFt} y2={runDraft.y2 * pxPerFt}
            stroke={RUN_KINDS[runDraft.tool].color} strokeDasharray="5 4" strokeWidth={Math.max(2, RUN_KINDS[runDraft.tool].width * pxPerFt)} strokeLinecap="round" opacity="0.8" />
        )}

        {/* setback overlay: estimated lot + buildable envelope */}
        {sb && (
          <g style={{ pointerEvents: "none" }} data-testid="setback-overlay">
            <rect x={sb.lot.x * pxPerFt} y={sb.lot.y * pxPerFt} width={sb.lot.w * pxPerFt} height={sb.lot.h * pxPerFt}
              fill="none" stroke="#6E7681" strokeWidth="1.5" strokeDasharray="2 3" />
            <text x={sb.lot.x * pxPerFt + 4} y={sb.lot.y * pxPerFt - 4} fill="#6E7681" fontSize="9" fontFamily="JetBrains Mono, monospace">PROPERTY LINE (est.)</text>
            <rect x={sb.env.x * pxPerFt} y={sb.env.y * pxPerFt} width={sb.env.w * pxPerFt} height={sb.env.h * pxPerFt}
              fill="#F59E0B" fillOpacity="0.06" stroke="#F59E0B" strokeWidth="2" strokeDasharray="8 5" data-testid="setback-envelope" />
            <text x={sb.env.x * pxPerFt + 4} y={sb.env.y * pxPerFt + 13} fill="#F59E0B" fontSize="9" fontWeight="700" fontFamily="JetBrains Mono, monospace">BUILDABLE · F{sb.front}′ S{sb.side}′ R{sb.rear}′</text>
            <text x={(sb.lot.x + sb.lot.w) * pxPerFt - 4} y={(sb.lot.y + sb.lot.h) * pxPerFt - 5} textAnchor="end" fill="#8B949E" fontSize="9" fontFamily="JetBrains Mono, monospace">{Math.round(sb.lot.w)}′ × {Math.round(sb.lot.h)}′</text>
            {!readOnly && setLot && [["nw", sb.lot.x, sb.lot.y], ["ne", sb.lot.x + sb.lot.w, sb.lot.y], ["sw", sb.lot.x, sb.lot.y + sb.lot.h], ["se", sb.lot.x + sb.lot.w, sb.lot.y + sb.lot.h]].map(([c, hx, hy]) => (
              <rect key={c} x={hx * pxPerFt - 5} y={hy * pxPerFt - 5} width="10" height="10" rx="2"
                fill="#0D1117" stroke="#38BDF8" strokeWidth="2" style={{ cursor: "nwse-resize", pointerEvents: "all" }}
                onMouseDown={(e) => startLotDrag(e, c)} data-testid={`lot-handle-${c}`} />
            ))}
          </g>
        )}

        {/* angled property-line boundary + buildable envelope */}
        {poly && (
          <g style={{ pointerEvents: "none" }} data-testid="boundary-overlay">
            <polygon points={pointsAttr(poly.map((p) => ({ x: p.x * pxPerFt, y: p.y * pxPerFt })))}
              fill="none" stroke="#F43F5E" strokeWidth="2" strokeDasharray="2 3" />
            {buildablePoly && (
              <polygon points={pointsAttr(buildablePoly.map((p) => ({ x: p.x * pxPerFt, y: p.y * pxPerFt })))}
                fill="#F59E0B" fillOpacity="0.06" stroke="#F59E0B" strokeWidth="2" strokeDasharray="8 5" data-testid="buildable-polygon" />
            )}
            {poly.map((v, i) => (
              <circle key={i} cx={v.x * pxPerFt} cy={v.y * pxPerFt} r="3.5" fill="#F43F5E" stroke="#0D1117" strokeWidth="1.5" />
            ))}
          </g>
        )}

        {/* draft room (turns red if it crosses the setback line) */}
        {draft && (() => {
          const dv = sbCalc && (draft.x < sbCalc.env.x - 0.01 || draft.y < sbCalc.env.y - 0.01 || draft.x + draft.w > sbCalc.env.x + sbCalc.env.w + 0.01 || draft.y + draft.h > sbCalc.env.y + sbCalc.env.h + 0.01);
          return (
            <g>
              <rect x={draft.x * pxPerFt} y={draft.y * pxPerFt} width={draft.w * pxPerFt} height={draft.h * pxPerFt}
                fill={dv ? "rgba(244,63,94,0.14)" : "rgba(56,189,248,0.12)"} stroke={dv ? "#F43F5E" : "#38BDF8"} strokeDasharray="4 4" strokeWidth="2" />
              {dv && <text x={draft.x * pxPerFt + draft.w * pxPerFt / 2} y={draft.y * pxPerFt - 4} fill="#F43F5E" fontSize="9" fontWeight="700" fontFamily="JetBrains Mono, monospace" textAnchor="middle" data-testid="draft-setback-warning">⚠ crosses setback</text>}
            </g>
          );
        })()}

        {/* items */}
        {blueprint.items.filter((it) => activeLayers[it.layer]).map((it) => {
          const meta = ITEM_KINDS[it.kind];
          const color = LAYERS[it.layer].accent;
          const sel = it.id === selectedId;
          if (meta.furniture) {
            const w = (it.w || meta.w) * pxPerFt;
            const h = (it.h || meta.h) * pxPerFt;
            const isBlocked = blocked.has(it.id);
            return (
              <g key={it.id} transform={`translate(${it.x * pxPerFt}, ${it.y * pxPerFt})`}
                onPointerDown={(e) => startItemDrag(e, it)} data-testid={`item-${it.id}`}
                style={{ cursor: (readOnly || commentMode) ? "default" : (tool === "select" ? "move" : "crosshair") }}>
                <rect width={w} height={h} rx="3" fill={`${color}22`} stroke={color} strokeWidth={sel ? 3 : 1.5} />
                {meta.stair && [...Array(6)].map((_, k) => (
                  <line key={k} x1="0" y1={(h / 6) * (k + 1)} x2={w} y2={(h / 6) * (k + 1)} stroke={color} strokeWidth="1" strokeOpacity="0.7" />
                ))}
                <text x={w / 2} y={h / 2 + 4} fill={color} fontSize="10" fontWeight="600" textAnchor="middle" fontFamily="Outfit, sans-serif" style={{ pointerEvents: "none" }}>{meta.label}</text>
                {isBlocked && (
                  <g style={{ pointerEvents: "none" }} data-testid={`blocked-item-${it.id}`}>
                    <rect x="-2" y="-2" width={w + 4} height={h + 4} rx="4" fill="none" stroke="#F43F5E" strokeWidth="2.5" strokeDasharray="5 3" />
                    <circle cx={w - 2} cy={2} r="8" fill="#F43F5E" />
                    <text x={w - 2} y={6} textAnchor="middle" fontSize="11" fontWeight="900" fill="#0D1117">!</text>
                  </g>
                )}
              </g>
            );
          }
          const R = 9;
          return (
            <g key={it.id} transform={`translate(${it.x * pxPerFt}, ${it.y * pxPerFt})`}
              onPointerDown={(e) => startItemDrag(e, it)} data-testid={`item-${it.id}`}
              style={{ cursor: (readOnly || commentMode) ? "default" : (tool === "select" ? "move" : "crosshair") }}>
              <circle r={R} fill="#0D1117" stroke={color} strokeWidth={sel ? 3 : 2} />
              <text y="4" fill={color} fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono, monospace" style={{ pointerEvents: "none" }}>{meta.label}</text>
            </g>
          );
        })}

        {/* comment pins */}
        {(comments || []).map((c, i) => (
          <g key={c.id || i} transform={`translate(${c.x * pxPerFt}, ${c.y * pxPerFt})`} data-testid={`comment-pin-${i}`} style={{ cursor: "help" }}>
            <circle r="11" fill="#38BDF8" stroke="#0D1117" strokeWidth="2" />
            <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#0D1117" style={{ pointerEvents: "none" }}>{i + 1}</text>
            <title>{`${c.author}: ${c.text}`}</title>
          </g>
        ))}
      </svg>
    </div>
  );
}
